import { AdminShell } from '@/features/admin/components/admin-shell.js';
import { RoleDetail } from '@/features/admin/components/roles/role-detail.js';
import { RoleForm } from '@/features/admin/components/roles/role-form.js';
import { RolesList } from '@/features/admin/components/roles/roles-list.js';
import { useAdminRole } from '@/features/admin/hooks/use-admin-roles.js';
import { useAuth } from '@/lib/auth/use-auth.js';
import { tokens } from '@/lib/tokens.js';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_admin/roles')({
  component: RolesPage,
});

type View = 'list' | 'create' | 'detail' | 'edit';

// B-3: sub-component that loads the role before rendering the edit form
function EditRoleView({
  tenantId,
  roleId,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  roleId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { data: role, isLoading } = useAdminRole(tenantId, roleId);

  if (isLoading || role === undefined) {
    return (
      <output
        style={{
          display: 'block',
          padding: '2rem',
          textAlign: 'center',
          color: tokens.colors.textMutedDark,
        }}
        aria-label="Loading role"
      >
        Loading…
      </output>
    );
  }

  return (
    <RoleForm tenantId={tenantId} existingRole={role} onSuccess={onSuccess} onCancel={onCancel} />
  );
}

function RolesPage() {
  const { user } = useAuth();
  const [view, setView] = useState<View>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const tenantId = user?.tenant ?? '';

  return (
    <AdminShell currentPath="/roles">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}
        >
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: tokens.colors.textDark,
              margin: 0,
            }}
          >
            Roles
          </h1>
          {view !== 'list' && (
            <button
              type="button"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: tokens.colors.primary,
                cursor: 'pointer',
                fontSize: '0.875rem',
                padding: 0,
              }}
              onClick={() => {
                setView('list');
                setSelectedId(null);
              }}
              aria-label="Back to list"
            >
              ← Back
            </button>
          )}
        </div>

        {view === 'list' && (
          <RolesList
            tenantId={tenantId}
            onSelect={(id) => {
              setSelectedId(id);
              setView('detail');
            }}
            onCreateClick={() => setView('create')}
          />
        )}

        {view === 'create' && (
          <RoleForm
            tenantId={tenantId}
            onSuccess={() => setView('list')}
            onCancel={() => setView('list')}
          />
        )}

        {view === 'detail' && selectedId !== null && (
          <RoleDetail tenantId={tenantId} roleId={selectedId} onEditClick={() => setView('edit')} />
        )}

        {/* B-3: load existing role and pass as existingRole prop — prevents createRole being called on edit */}
        {view === 'edit' && selectedId !== null && (
          <EditRoleView
            tenantId={tenantId}
            roleId={selectedId}
            onSuccess={() => setView('detail')}
            onCancel={() => setView('detail')}
          />
        )}
      </div>
    </AdminShell>
  );
}
